<template>
  <div class="ticket-system">
    <div class="ticket-categories">
      <div 
        v-for="(category, categoryIndex) in ticketCategories" 
        :key="categoryIndex"
        class="category-section"
      >
        <div class="category-header" :style="{ '--category-color': category.color }">
          <div class="category-icon">
            <i :class="category.icon"></i>
          </div>
          <div class="category-info">
            <div class="category-name">{{ category.name }}</div>
          </div>
        </div>
        
        <div class="ticket-types">
          <div 
            v-for="(type, typeIndex) in category.types" 
            :key="`${categoryIndex}-${typeIndex}`"
            class="ticket-type"
            :style="{ '--category-color': category.color }"
          >
            <div class="type-header">
              <span class="type-name">{{ type.name }}</span>
              <span class="virtual-transfer" :class="{ 'supported': type.virtualTransfer }">
                {{ type.virtualTransfer ? '支持虚拟换乘' : '不支持虚拟换乘' }}
              </span>
            </div>
            <div class="type-usage">{{ type.usage }}</div>
            <div class="type-method">{{ type.method }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>

interface TicketType {
  name: string
  usage: string
  method: string
  virtualTransfer: boolean
}

interface TicketCategory {
  name: string
  icon: string
  color: string
  types: TicketType[]
}

const ticketCategories: TicketCategory[] = [
  {
    name: '单次票',
    icon: 'fas fa-ticket',
    color: '#ef4444',
    types: [
      {
        name: '实体卡单程票',
        usage: '按起讫点计价，仅限当日单次乘坐。进站：闸机感应区刷卡，出站插卡回收。',
        method: '不支持虚拟换乘，停留超过4小时需处理',
        virtualTransfer: false
      },
      {
        name: '纸质票单程票',
        usage: '按起讫点计价，仅限当日单次乘坐。进/出站：闸机扫码区扫码，出站不回收。',
        method: '不支持虚拟换乘，停留超过4小时需处理',
        virtualTransfer: false
      }
    ]
  },
  {
    name: '公共交通卡',
    icon: 'fas fa-credit-card',
    color: '#0085C3',
    types: [
      {
        name: '实体卡或智能设备',
        usage: '实体卡或加载交通卡的手机/手表/手环，可反复储值使用。',
        method: '进/出站：闸机感应区刷卡，出站不回收，支持虚拟换乘（连续计费）',
        virtualTransfer: true
      }
    ]
  },
  {
    name: '旅游通票(时效票)',
    icon: 'fas fa-plane',
    color: '#10b981',
    types: [
      {
        name: '一日票',
        usage: '首次进站后24小时内不限次乘坐。不适用线路：磁浮线、2024年及以后开通的市域线。',
        method: '进/出站刷卡，出站不回收，支持虚拟换乘，同站点5分钟内禁止再进站',
        virtualTransfer: true
      },
      {
        name: '三日票',
        usage: '首次进站后72小时内不限次乘坐。不适用线路：同一日票。',
        method: '使用方式同一日票，同站点5分钟内禁止再进站',
        virtualTransfer: true
      }
    ]
  },
  {
    name: '特殊功能票',
    icon: 'fas fa-star',
    color: '#f59e0b',
    types: [
      {
        name: '纪念票',
        usage: '按票面有效期使用，图案具纪念意义。进/出站：闸机感应区刷卡。',
        method: '出站不回收，支持虚拟换乘',
        virtualTransfer: true
      },
      {
        name: '票务处理专用票',
        usage: '首次进站后24小时内不限次乘坐，自换取日起1个月内有效。不适用线路：同旅游通票。',
        method: '使用方式同一日票，同站点5分钟内禁止再进站',
        virtualTransfer: true
      },
      {
        name: '保通卡',
        usage: '仅限上海市户籍70岁以上老人。免费时段：工作日非高峰（禁用7:00-9:00、17:00-19:00）、节假日全天。',
        method: '禁用线路：高速公交、机场线、旅游线、磁浮线、2024年及以后市域线。专用通道进出站刷卡感应，支持虚拟换乘',
        virtualTransfer: true
      },
      {
        name: '地铁磁浮联票',
        usage: '地铁部分：24小时内不限次乘坐（不含2024年及以后市域线）；磁浮部分：含普通席单程或双程。',
        method: '进/出站：闸机感应区刷卡，出站不回收，支持虚拟换乘',
        virtualTransfer: true
      }
    ]
  },
  {
    name: '移动支付(二维码)',
    icon: 'fas fa-mobile-alt',
    color: '#8b5cf6',
    types: [
      {
        name: 'Metro大都会乘车码',
        usage: '通过App、支付宝、微信开通。进/出站：闸机扫码区扫码。',
        method: '支持虚拟换乘',
        virtualTransfer: true
      },
      {
        name: '随申码/上海公共交通乘车码',
        usage: '通过随申办、交通卡App、随申行开通。使用方式：同Metro大都会。',
        method: '支持虚拟换乘',
        virtualTransfer: true
      }
    ]
  },
  {
    name: '创新支付',
    icon: 'fas fa-university',
    color: '#06b6d4',
    types: [
      {
        name: '银行卡/数字人民币硬钱包',
        usage: '支持银联、Visa、Mastercard、American Express、JCB芯片卡及手机Pay；支持数字人民币硬钱包。',
        method: '进/出站：直接闸机刷卡，需开通小额免密支付并保证余额充足',
        virtualTransfer: false
      }
    ]
  },
  {
    name: '异地交通卡/码',
    icon: 'fas fa-globe',
    color: '#64748b',
    types: [
      {
        name: '非上海发行的全国互通卡/码',
        usage: '进/出站：异地卡刷卡感应，异地码扫码感应。',
        method: '不支持虚拟换乘（需重新购票），规则详询发卡机构',
        virtualTransfer: false
      }
    ]
  }
]
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;500;600;700&display=swap');

.ticket-system {
  width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 12px;
  overflow: hidden;
  font-family: 'Noto Serif SC', serif;
}

.ticket-categories {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
  background: white;
}

.category-section {
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.category-header {
  display: flex;
  align-items: center;
  padding: 16px;
  background: white;
  border-bottom: 2px solid var(--category-color);
  gap: 0;
  justify-content: flex-start;
}

.category-icon {
  font-size: 24px;
  color: var(--category-color);
  margin-right: 12px;
}

.category-info {
  flex: 1;
}

.category-name {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  font-family: 'Noto Serif SC', serif;
}

.ticket-types {
  display: grid;
  gap: 12px;
  padding: 16px;
}

.ticket-type {
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.ticket-type:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.type-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.type-name {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  font-family: 'Noto Serif SC', serif;
}

.virtual-transfer {
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 500;
}

.virtual-transfer.supported {
  background: #dcfce7;
  color: #166534;
}

.virtual-transfer:not(.supported) {
  background: #fef2f2;
  color: #991b1b;
}

.type-usage {
  font-size: 13px;
  color: #374151;
  margin-bottom: 8px;
  line-height: 1.5;
  font-weight: 500;
  font-family: 'Noto Serif SC', serif;
}

.type-method {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  font-family: 'Noto Serif SC', serif;
}

</style>